import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, CheckCircle, Code, Smartphone, Cloud, Layout,
  Shield, Zap, Server, Database, Globe, Target, Layers,
  Cpu, Terminal, Workflow, BarChart2, GitBranch, Briefcase,
  ChevronUp, ChevronDown, Lock, Search, Bell, Users,
} from "lucide-react";
import PortfolioNavbar from "./PortfolioNavbar";
import PortfolioFooter from "./PortfolioFooter";
import SDLCInfographic from "./SDLCInfographic";

/* ─── HERO SLIDES ─── */
const heroSlides = [
  {
    eyebrow: "Custom Software Engineering",
    line1: "Enterprise Applications",
    line2: "Built to Last",
    sub: "Bespoke web, backend, and SaaS platforms engineered for performance, security, and long-term scalability.",
    image: "/images/photo-1498050108023-c5249f4df085.jpg",
    cta: { label: "Explore Solutions", path: "/software-solutions" },
    accent: "from-orange-400 to-amber-300",
  },
  {
    eyebrow: "Cloud Architecture & DevOps",
    line1: "Cloud-Native",
    line2: "Infrastructure",
    sub: "High-availability deployments on AWS, Azure & GCP with automated CI/CD pipelines and 99.9% uptime SLA.",
    image: "/images/photo-1451187580459-43490279c0fa.jpg",
    cta: { label: "Strategy & Architecture", path: "/strategy-and-architecture" },
    accent: "from-blue-400 to-indigo-300",
  },
  {
    eyebrow: "Mobile App Development",
    line1: "Native & Hybrid",
    line2: "Mobile Apps",
    sub: "Cross-platform iOS and Android apps with React Native & Flutter — from concept to App Store launch.",
    image: "/images/photo-1512941937669-90a1b58e7e9c.jpg",
    cta: { label: "View Our Work", path: "/case-studies" },
    accent: "from-emerald-400 to-teal-300",
  },
];

/* ─── DATA ─── */
const softwareStats = [
  { value: "100+", label: "Projects Delivered" },
  { value: "50+", label: "Engineers" },
  { value: "99.9%", label: "Uptime SLA" },
  { value: "6+", label: "Years Experience" },
];

const serviceCards = [
  {
    icon: <Code className="w-6 h-6 text-orange-400" />,
    title: "Custom Software Development",
    desc: "Bespoke web and backend solutions engineered for performance, security, and long-term scalability.",
    path: "/software-solutions",
    badge: "Full-Stack",
  },
  {
    icon: <Smartphone className="w-6 h-6 text-orange-400" />,
    title: "Native & Hybrid Mobile Apps",
    desc: "End-to-end mobile development for iOS and Android — from concept to App Store and Play Store launch.",
    path: "/software-solutions",
    badge: "iOS & Android",
  },
  {
    icon: <Cloud className="w-6 h-6 text-orange-400" />,
    title: "Cloud Architecture & DevOps",
    desc: "High-availability cloud infrastructure on AWS, Azure & GCP with automated CI/CD pipelines.",
    path: "/strategy-and-architecture",
    badge: "AWS · Azure · GCP",
  },
  {
    icon: <Database className="w-6 h-6 text-orange-400" />,
    title: "Enterprise ERP & CRM",
    desc: "Custom management systems tailored to complex organizational workflows and data requirements.",
    path: "/software-solutions",
    badge: "Enterprise",
  },
];

const weBuildFeatures = [
  "React, Next.js & Node.js full-stack applications",
  "Scalable REST & GraphQL API architecture",
  "React Native & Flutter mobile apps",
  "Real-time features with WebSocket & Firebase",
  "Progressive Web Apps (PWA)",
  "Multi-tenant SaaS platform engineering",
];

const weArchitectFeatures = [
  "AWS, Azure & GCP cloud deployments",
  "Kubernetes & Docker containerisation",
  "Custom ERP & CRM system development",
  "Microservices & event-driven architecture",
  "Data pipelines, analytics & BI dashboards",
  "99.9% uptime SLA with active monitoring",
];

const weDeployFeatures = [
  "End-to-end CI/CD pipeline setup",
  "Infrastructure as Code with Terraform",
  "Pixel-perfect UI/UX design systems",
  "Accessibility-first component libraries",
  "Brand identity & digital product design",
  "Performance audits & load-time optimisation",
];

const processSteps = [
  {
    icon: <Target className="w-6 h-6 text-orange-500" />,
    num: "01",
    title: "Discovery & Analysis",
    desc: "Deep-diving into your requirements to define project scope, risks, and a clear technical roadmap.",
  },
  {
    icon: <Layout className="w-6 h-6 text-orange-500" />,
    num: "02",
    title: "System Design",
    desc: "Architecting data flow, infrastructure topology, and user journeys before a single line of code is written.",
  },
  {
    icon: <Code className="w-6 h-6 text-orange-500" />,
    num: "03",
    title: "Iterative Development",
    desc: "Sprint-based coding with regular demos, continuous integration, and real-time progress visibility.",
  },
  {
    icon: <Shield className="w-6 h-6 text-orange-500" />,
    num: "04",
    title: "Testing & Validation",
    desc: "Comprehensive unit, integration, and user-acceptance testing across all environments.",
  },
  {
    icon: <Zap className="w-6 h-6 text-orange-500" />,
    num: "05",
    title: "Deployment & Launch",
    desc: "Zero-downtime production releases with automated rollbacks and live health checks.",
  },
  {
    icon: <Server className="w-6 h-6 text-orange-500" />,
    num: "06",
    title: "Support & Scaling",
    desc: "Ongoing monitoring, performance tuning, and feature enhancements after go-live.",
  },
];

const frontendStack = [
  { icon: <Layout className="w-5 h-5 text-white" />, title: "React & Next.js", color: "from-blue-500 to-blue-700" },
  { icon: <Zap className="w-5 h-5 text-white" />, title: "Tailwind CSS", color: "from-cyan-500 to-cyan-700" },
  { icon: <Layers className="w-5 h-5 text-white" />, title: "TypeScript", color: "from-blue-600 to-indigo-700" },
  { icon: <GitBranch className="w-5 h-5 text-white" />, title: "Redux & Context", color: "from-purple-500 to-purple-700" },
];

const backendStack = [
  { icon: <Server className="w-5 h-5 text-white" />, title: "Node.js & Express", color: "from-green-500 to-green-700" },
  { icon: <Database className="w-5 h-5 text-white" />, title: "PostgreSQL & MongoDB", color: "from-blue-500 to-blue-700" },
  { icon: <Lock className="w-5 h-5 text-white" />, title: "Redis", color: "from-red-500 to-red-700" },
  { icon: <Terminal className="w-5 h-5 text-white" />, title: "Python (FastAPI)", color: "from-yellow-500 to-orange-600" },
];

const devopsStack = [
  { icon: <Cloud className="w-5 h-5 text-white" />, title: "AWS & Azure", color: "from-orange-500 to-orange-700" },
  { icon: <Cpu className="w-5 h-5 text-white" />, title: "Docker & Kubernetes", color: "from-blue-500 to-blue-700" },
  { icon: <Workflow className="w-5 h-5 text-white" />, title: "GitHub Actions", color: "from-gray-600 to-gray-800" },
  { icon: <Shield className="w-5 h-5 text-white" />, title: "Terraform", color: "from-purple-500 to-purple-700" },
];

const strategyPoints = [
  { icon: <Cpu className="w-5 h-5 text-orange-400" />, title: "System Design & Architecture", detail: "Designing scalable, high-availability architectures tailored to your business needs." },
  { icon: <Database className="w-5 h-5 text-orange-400" />, title: "Database Modeling & API Strategy", detail: "Optimizing data structures and designing REST/GraphQL APIs for seamless integration." },
  { icon: <Terminal className="w-5 h-5 text-orange-400" />, title: "Tech Stack Selection", detail: "Choosing the right tools and frameworks to ensure long-term maintainability and performance." },
  { icon: <Cloud className="w-5 h-5 text-orange-400" />, title: "Cloud & Infrastructure Planning", detail: "Strategizing AWS/Azure/GCP deployments with focus on security, cost, and scalability." },
];

const monitoringTools = [
  { icon: <BarChart2 className="w-5 h-5 text-orange-400" />, title: "Grafana & Prometheus", detail: "Real-time metrics and visualization for system performance monitoring." },
  { icon: <Search className="w-5 h-5 text-orange-400" />, title: "Sentry & LogRocket", detail: "Advanced error tracking and session replay for rapid issue resolution." },
  { icon: <Globe className="w-5 h-5 text-orange-400" />, title: "New Relic", detail: "Full-stack observability to monitor application health and user experience." },
  { icon: <Bell className="w-5 h-5 text-orange-400" />, title: "Jira & Confluence", detail: "Agile project management and documentation for streamlined collaboration." },
];

const testimonials = [
  { quote: "Winsil transformed our legacy ERP into a modern cloud platform. Delivery was on-time and the code quality was exceptional.", name: "Arjun Mehta", role: "CTO, FinVault Technologies", initials: "AM", color: "bg-blue-600" },
  { quote: "Their agile process and transparent communication made us feel like genuine partners. The SaaS platform handles 10× our original load.", name: "Priya Sharma", role: "Product Lead, MedCore AI", initials: "PS", color: "bg-emerald-600" },
  { quote: "Security, scalability, and speed — Winsil delivered all three. Our platform supports 100k concurrent users without breaking a sweat.", name: "Rahul Verma", role: "Engineering Director, RetailEdge", initials: "RV", color: "bg-orange-600" },
];

/* ─── HERO COMPONENT ─── */
const SoftwareHero = () => {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const navigate = useNavigate();

  const goTo = useCallback((index) => {
    if (animating) return;
    setAnimating(true);
    setCurrent(index);
    setTimeout(() => setAnimating(false), 700);
  }, [animating]);

  const prev = () => goTo((current - 1 + heroSlides.length) % heroSlides.length);
  const next = useCallback(() => goTo((current + 1) % heroSlides.length), [current, goTo]);

  useEffect(() => {
    const t = setInterval(next, 5500);
    return () => clearInterval(t);
  }, [next]);

  return (
    <div className="relative h-screen min-h-[640px] bg-gray-950 overflow-hidden">
      <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-orange-600/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />

      {heroSlides.map((s, i) => (
        <div
          key={i}
          className="absolute inset-0 flex items-center"
          style={{ transform: `translateY(${(i - current) * 100}%)`, transition: "transform 0.7s cubic-bezier(0.77,0,0.18,1)", zIndex: i === current ? 10 : 5 }}
        >
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-16 w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">

              {/* LEFT — text content */}
              <div>
                <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-orange-500/20 border border-orange-400/40 mb-6">
                  <span className="w-2 h-2 rounded-full bg-orange-400 mr-2 animate-pulse" />
                  <span className="text-orange-300 text-xs font-black uppercase tracking-[0.2em]">{s.eyebrow}</span>
                </div>
                <h1 className="text-5xl sm:text-6xl font-black text-white leading-[1.05] mb-5">
                  {s.line1}<br />
                  <span className={`bg-gradient-to-r ${s.accent} bg-clip-text text-transparent`}>{s.line2}</span>
                </h1>
                <p className="text-white/55 text-base leading-relaxed mb-8 max-w-lg">{s.sub}</p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button onClick={() => navigate(s.cta.path)} className="inline-flex items-center justify-center px-7 py-3.5 text-sm font-black uppercase tracking-wider rounded-lg bg-orange-500 text-white hover:bg-orange-400 transition-all duration-300 hover:scale-105 shadow-lg shadow-orange-500/30">
                    {s.cta.label} <ArrowRight className="w-4 h-4 ml-2" />
                  </button>
                  <button onClick={() => navigate("/contact")} className="inline-flex items-center justify-center px-7 py-3.5 text-sm font-bold uppercase tracking-wider rounded-lg border border-white/20 text-white/70 hover:border-orange-400 hover:text-white transition-all duration-300">
                    Get a Quote
                  </button>
                </div>
              </div>

              {/* RIGHT — image panel */}
              <div className="hidden lg:block relative">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl group h-[480px]">
                  <img
                    src={s.image}
                    alt={s.line1}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading={i === 0 ? "eager" : "lazy"}
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-gray-950/60 via-transparent to-orange-500/10" />
                  <div className="absolute bottom-5 left-5 bg-white/95 backdrop-blur-sm rounded-xl px-5 py-3 shadow-xl">
                    <p className="text-2xl font-black text-orange-600">100+</p>
                    <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Projects Delivered</p>
                  </div>
                  <div className="absolute top-5 right-5 bg-orange-500/90 backdrop-blur-sm text-white rounded-full px-4 py-1.5 text-xs font-black tracking-wider shadow-lg">
                    {s.eyebrow}
                  </div>
                </div>
                <div className="absolute -bottom-4 -right-4 bg-gray-950 border border-orange-500/30 rounded-2xl px-5 py-3 shadow-xl">
                  <p className="text-white text-sm font-black">50+ Engineers</p>
                  <p className="text-orange-400 text-xs">Full-Stack Team</p>
                </div>
              </div>

            </div>
          </div>
        </div>
      ))}

      {/* Vertical nav */}
      <div className="absolute right-6 sm:right-10 top-1/2 -translate-y-1/2 z-30 flex flex-col items-center gap-3">
        <button onClick={prev} className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-orange-500 hover:border-orange-500 transition-all duration-300">
          <ChevronUp className="w-4 h-4" />
        </button>
        <div className="flex flex-col items-center gap-2 py-1">
          {heroSlides.map((_, i) => (
            <button key={i} onClick={() => goTo(i)} className="rounded-full transition-all duration-300" style={{ width: "0.55rem", height: i === current ? "2rem" : "0.55rem", backgroundColor: i === current ? "#f97316" : "rgba(255,255,255,0.3)" }} />
          ))}
        </div>
        <button onClick={next} className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-orange-500 hover:border-orange-500 transition-all duration-300">
          <ChevronDown className="w-4 h-4" />
        </button>
      </div>

      <div className="absolute bottom-8 left-6 sm:left-16 z-30 flex items-end gap-1 text-white/30">
        <span className="text-4xl font-black text-white/70 leading-none">{String(current + 1).padStart(2, "0")}</span>
        <span className="text-lg mb-1">/</span>
        <span className="text-lg mb-1">{String(heroSlides.length).padStart(2, "0")}</span>
      </div>

      {/* Floating tech labels */}
      <div className="absolute bottom-8 right-16 sm:right-24 z-30 hidden lg:flex items-center gap-3">
        {["React", "Node.js", "AWS"].map((t) => (
          <span key={t} className="px-3 py-1 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full text-white/50 text-xs font-bold">{t}</span>
        ))}
      </div>
    </div>
  );
};

/* ─── MAIN PAGE ─── */
const SoftwareLanding = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <PortfolioNavbar />
      <SoftwareHero />

      {/* ── Stats Strip ── */}
      <div className="bg-gray-900 border-y border-white/10 py-12 px-4">
        <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          {softwareStats.map((s) => (
            <div key={s.label}>
              <p className="text-3xl sm:text-4xl font-black text-orange-400 mb-1">{s.value}</p>
              <p className="text-xs font-bold uppercase tracking-widest text-white/40">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Services Grid ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-b-4 border-orange-500">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.35em] text-center mb-3 text-orange-500">What We Build</p>
          <h2 className="text-4xl sm:text-5xl font-black text-gray-900 text-center mb-4">Software Development Services</h2>
          <p className="text-gray-500 text-base text-center max-w-2xl mx-auto mb-14">
            End-to-end engineering — from custom web apps to cloud-native enterprise platforms.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {serviceCards.map((card) => (
              <div key={card.title} onClick={() => navigate(card.path)} className="group bg-white border border-gray-100 rounded-2xl p-7 shadow-md hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 to-amber-400 rounded-t-2xl" />
                <div className="w-12 h-12 bg-orange-50 border border-orange-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-orange-100 transition-colors">{card.icon}</div>
                <span className="inline-block px-2 py-0.5 bg-orange-50 text-orange-600 text-[10px] font-black uppercase tracking-wider rounded mb-3">{card.badge}</span>
                <h3 className="text-sm font-extrabold text-gray-900 mb-2">{card.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed mb-4">{card.desc}</p>
                <div className="inline-flex items-center text-xs font-bold text-orange-500 group-hover:text-orange-600 transition-colors">
                  Learn More <ArrowRight className="w-3.5 h-3.5 ml-1.5 group-hover:translate-x-1 transition-transform duration-200" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── We Build ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.25em] text-orange-500 mb-3">We Build</p>
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 leading-tight mb-6">
              Marvelous <span className="text-orange-500">WEB</span> &amp; <span className="text-purple-700">MOBILE</span> Apps
            </h2>
            <p className="text-gray-600 text-base leading-relaxed mb-8 max-w-lg">
              From pixel-perfect frontends to robust backend systems, we engineer full-stack products that users love and businesses rely on.
            </p>
            <ul className="space-y-3 mb-10">
              {weBuildFeatures.map((f) => (
                <li key={f} className="flex items-center gap-3">
                  <CheckCircle className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  <span className="text-gray-700 text-sm font-medium">{f}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={() => navigate("/software-solutions")} className="inline-flex items-center justify-center px-7 py-3.5 text-sm font-black uppercase tracking-wider rounded-lg bg-orange-500 text-white hover:bg-orange-400 transition-all duration-300 hover:scale-105 shadow-lg shadow-orange-500/20">
                Explore Solutions <ArrowRight className="w-4 h-4 ml-2" />
              </button>
              <button onClick={() => navigate("/case-studies")} className="inline-flex items-center justify-center px-7 py-3.5 text-sm font-bold uppercase tracking-wider rounded-lg border-2 border-gray-300 text-gray-700 hover:border-orange-500 hover:text-orange-600 transition-all duration-300">
                View Case Studies
              </button>
            </div>
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
            <img src="/images/photo-1522202176988-66273c2fd55f.jpg" alt="Web and mobile development" className="w-full h-[460px] object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-tr from-gray-900/60 via-transparent to-orange-500/10" />
            <div className="absolute bottom-5 left-5 bg-white/95 backdrop-blur-sm rounded-xl px-5 py-3 shadow-xl">
              <p className="text-2xl font-black text-orange-600">100+</p>
              <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Apps Delivered</p>
            </div>
            <div className="absolute top-5 right-5 bg-orange-500/90 text-white rounded-full px-4 py-1.5 text-xs font-black tracking-wider">Full-Stack</div>
          </div>
        </div>
      </div>

      {/* ── We Architect ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl group order-2 lg:order-1">
            <img src="/images/photo-1451187580459-43490279c0fa.jpg" alt="Cloud architecture" className="w-full h-[460px] object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-tr from-blue-900/60 via-transparent to-orange-500/10" />
            <div className="absolute bottom-5 right-5 bg-white/95 backdrop-blur-sm rounded-xl px-5 py-3 shadow-xl">
              <p className="text-2xl font-black text-blue-700">99.9%</p>
              <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Uptime SLA</p>
            </div>
            <div className="absolute top-5 left-5 bg-blue-600/90 text-white rounded-full px-4 py-1.5 text-xs font-black tracking-wider">Cloud-Native</div>
          </div>
          <div className="order-1 lg:order-2">
            <p className="text-xs font-black uppercase tracking-[0.25em] text-blue-500 mb-3">We Architect</p>
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 leading-tight mb-6">
              <span className="text-blue-500">CLOUD</span> &amp; Enterprise Infrastructure
            </h2>
            <p className="text-gray-600 text-base leading-relaxed mb-8 max-w-lg">
              Robust, scalable system architectures designed for enterprise loads — microservices, containerization, and cloud deployments done right.
            </p>
            <ul className="space-y-3 mb-10">
              {weArchitectFeatures.map((f) => (
                <li key={f} className="flex items-center gap-3">
                  <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  <span className="text-gray-700 text-sm font-medium">{f}</span>
                </li>
              ))}
            </ul>
            <button onClick={() => navigate("/strategy-and-architecture")} className="inline-flex items-center justify-center px-7 py-3.5 text-sm font-black uppercase tracking-wider rounded-lg bg-blue-600 text-white hover:bg-blue-500 transition-all duration-300 hover:scale-105 shadow-lg shadow-blue-600/20">
              View Architecture Services <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>

      {/* ── We Deploy ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.25em] text-orange-500 mb-3">We Deploy</p>
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 leading-tight mb-6">
              <span className="text-orange-500">DEVOPS</span> &amp; <span className="text-purple-700">DESIGN</span>
            </h2>
            <p className="text-gray-600 text-base leading-relaxed mb-8 max-w-lg">
              Rock-solid CI/CD pipelines paired with award-worthy UI/UX — we deliver software that ships fast and looks exceptional.
            </p>
            <ul className="space-y-3 mb-10">
              {weDeployFeatures.map((f) => (
                <li key={f} className="flex items-center gap-3">
                  <CheckCircle className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  <span className="text-gray-700 text-sm font-medium">{f}</span>
                </li>
              ))}
            </ul>
            <button onClick={() => navigate("/tech-stack")} className="inline-flex items-center justify-center px-7 py-3.5 text-sm font-black uppercase tracking-wider rounded-lg bg-purple-700 text-white hover:bg-purple-600 transition-all duration-300 hover:scale-105 shadow-lg shadow-purple-700/20">
              See Our Tech Stack <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
            <img src="/images/photo-1461749280684-dccba630e2f6.jpg" alt="DevOps and design" className="w-full h-[460px] object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-tr from-purple-900/60 via-transparent to-orange-500/10" />
            <div className="absolute bottom-5 left-5 bg-white/95 backdrop-blur-sm rounded-xl px-5 py-3 shadow-xl">
              <p className="text-2xl font-black text-purple-800">50+</p>
              <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Engineers</p>
            </div>
            <div className="absolute top-5 right-5 bg-purple-600/90 text-white rounded-full px-4 py-1.5 text-xs font-black tracking-wider">CI/CD + Design</div>
          </div>
        </div>
      </div>

      {/* ── Development Process ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.35em] text-center mb-3 text-orange-500">How We Work</p>
          <h2 className="text-4xl font-black text-gray-900 text-center mb-4">Our Development Process</h2>
          <p className="text-gray-500 text-base text-center max-w-xl mx-auto mb-14">
            A proven 6-stage delivery framework that keeps projects on time, on budget, and on spec.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {processSteps.map((step) => (
              <div key={step.num} className="relative bg-white border border-gray-100 rounded-2xl p-7 shadow-md hover:shadow-lg transition-shadow duration-300 group">
                <div className="absolute top-5 right-5 text-4xl font-black text-gray-100 select-none group-hover:text-orange-100 transition-colors">{step.num}</div>
                <div className="w-12 h-12 bg-orange-50 border border-orange-100 rounded-xl flex items-center justify-center mb-4">{step.icon}</div>
                <h3 className="text-sm font-extrabold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>

          {/* SDLC Diagram */}
          <div className="flex justify-center">
            <SDLCInfographic />
          </div>
        </div>
      </div>

      {/* ── Tech Stack ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.35em] text-center mb-3 text-orange-400">Technologies We Use</p>
          <h2 className="text-4xl font-black text-white text-center mb-4">Our Tech Stack</h2>
          <p className="text-white/45 text-base text-center max-w-xl mx-auto mb-14">
            Battle-tested tools and frameworks chosen for performance, reliability, and long-term maintainability.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Frontend */}
            <div>
              <p className="text-xs font-black uppercase tracking-wider text-orange-400 mb-5 text-center">Frontend</p>
              <div className="space-y-3">
                {frontendStack.map((t) => (
                  <div key={t.title} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-5 py-3">
                    <div className={`w-8 h-8 bg-gradient-to-br ${t.color} rounded-lg flex items-center justify-center flex-shrink-0`}>{t.icon}</div>
                    <span className="text-white/70 text-sm font-semibold">{t.title}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Backend */}
            <div>
              <p className="text-xs font-black uppercase tracking-wider text-orange-400 mb-5 text-center">Backend & Database</p>
              <div className="space-y-3">
                {backendStack.map((t) => (
                  <div key={t.title} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-5 py-3">
                    <div className={`w-8 h-8 bg-gradient-to-br ${t.color} rounded-lg flex items-center justify-center flex-shrink-0`}>{t.icon}</div>
                    <span className="text-white/70 text-sm font-semibold">{t.title}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* DevOps */}
            <div>
              <p className="text-xs font-black uppercase tracking-wider text-orange-400 mb-5 text-center">Cloud & DevOps</p>
              <div className="space-y-3">
                {devopsStack.map((t) => (
                  <div key={t.title} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-5 py-3">
                    <div className={`w-8 h-8 bg-gradient-to-br ${t.color} rounded-lg flex items-center justify-center flex-shrink-0`}>{t.icon}</div>
                    <span className="text-white/70 text-sm font-semibold">{t.title}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center mt-10">
            <button onClick={() => navigate("/tech-stack")} className="inline-flex items-center justify-center px-7 py-3.5 text-sm font-black uppercase tracking-wider rounded-lg border border-orange-500/50 text-orange-400 hover:bg-orange-500 hover:text-white transition-all duration-300">
              Full Tech Stack Details <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>

      {/* ── Strategy & Architecture ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.35em] text-center mb-3 text-orange-500">Engineering Excellence</p>
          <h2 className="text-4xl font-black text-gray-900 text-center mb-14">Strategy & Architecture</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-16">
            {strategyPoints.map((s) => (
              <div key={s.title} className="flex items-start gap-4 bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="w-10 h-10 bg-orange-50 border border-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">{s.icon}</div>
                <div>
                  <h4 className="text-gray-900 font-extrabold mb-1">{s.title}</h4>
                  <p className="text-gray-500 text-sm leading-relaxed">{s.detail}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Monitoring Tools */}
          <p className="text-xs font-black uppercase tracking-[0.35em] text-center mb-8 text-orange-500">Observability & Tooling</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {monitoringTools.map((t) => (
              <div key={t.title} className="flex items-start gap-4 bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="w-10 h-10 bg-orange-50 border border-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">{t.icon}</div>
                <div>
                  <h4 className="text-gray-900 font-extrabold mb-1">{t.title}</h4>
                  <p className="text-gray-500 text-sm leading-relaxed">{t.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Testimonials ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.35em] text-center mb-3 text-orange-500">Client Feedback</p>
          <h2 className="text-4xl font-black text-gray-900 text-center mb-14">What Our Clients Say</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-gray-50 border border-gray-100 rounded-2xl p-7 shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="flex mb-3">
                  {[...Array(5)].map((_, i) => <span key={i} className="text-orange-400 text-sm">★</span>)}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-6 italic">"{t.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${t.color} rounded-full flex items-center justify-center text-white text-sm font-black`}>{t.initials}</div>
                  <div>
                    <p className="text-gray-900 font-extrabold text-sm">{t.name}</p>
                    <p className="text-gray-400 text-xs">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Delivery Models ── */}
      <div className="py-16 px-4 sm:px-6 lg:px-8 bg-orange-50 border-t border-orange-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm flex items-start gap-5">
              <div className="w-12 h-12 bg-orange-50 border border-orange-100 rounded-xl flex items-center justify-center flex-shrink-0"><Briefcase className="w-6 h-6 text-orange-500" /></div>
              <div>
                <h3 className="text-gray-900 font-extrabold text-lg mb-2">Enterprise Solutions</h3>
                <p className="text-gray-500 text-sm leading-relaxed">Large-scale software systems designed for complex organizational workflows. ERP, CRM, data platforms, and more — built for thousands of concurrent users.</p>
              </div>
            </div>
            <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm flex items-start gap-5">
              <div className="w-12 h-12 bg-orange-50 border border-orange-100 rounded-xl flex items-center justify-center flex-shrink-0"><Zap className="w-6 h-6 text-orange-500" /></div>
              <div>
                <h3 className="text-gray-900 font-extrabold text-lg mb-2">Startup MVPs</h3>
                <p className="text-gray-500 text-sm leading-relaxed">Rapid development of core features to validate product-market fit quickly. Ship in 8–12 weeks with a scalable codebase ready to grow with your users.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Team CTA ── */}
      <div className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-900">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-8">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.3em] text-orange-400 mb-2">Our Team</p>
            <h2 className="text-3xl font-black text-white mb-2">50+ Engineers Ready to Build</h2>
            <p className="text-white/50 text-sm max-w-lg">Full-stack, mobile, cloud, and DevOps specialists — available as dedicated teams or on a per-project basis.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <button onClick={() => navigate("/contact")} className="inline-flex items-center justify-center px-7 py-3.5 text-sm font-black uppercase tracking-wider rounded-lg bg-orange-500 text-white hover:bg-orange-400 transition-all duration-300 hover:scale-105 shadow-lg">
              Start a Project <ArrowRight className="w-4 h-4 ml-2" />
            </button>
            <button onClick={() => navigate("/about-us")} className="inline-flex items-center justify-center px-7 py-3.5 text-sm font-bold uppercase tracking-wider rounded-lg border border-white/20 text-white/70 hover:border-orange-400 hover:text-white transition-all duration-300">
              <Users className="w-4 h-4 mr-2" /> Meet the Team
            </button>
          </div>
        </div>
      </div>

      {/* ── Pre-footer CTA ── */}
      <div className="relative bg-orange-900 py-20 px-4 overflow-hidden text-white text-center">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <div className="relative max-w-3xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.3em] text-orange-300 mb-4">Ready to Build?</p>
          <h2 className="text-4xl sm:text-5xl font-black mb-6">Let's Engineer Your Next Product</h2>
          <p className="text-white/65 text-lg mb-10 max-w-xl mx-auto">From MVP to enterprise-scale platform — tell us what you're building and we'll make it happen.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigate("/contact")} className="inline-flex items-center justify-center px-8 py-4 text-sm font-black uppercase tracking-wider rounded-lg bg-white text-orange-900 hover:bg-orange-50 transition-all duration-300 hover:scale-105 shadow-xl">
              Get a Free Consultation <ArrowRight className="w-4 h-4 ml-2" />
            </button>
            <button onClick={() => navigate("/software-solutions")} className="inline-flex items-center justify-center px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-lg border-2 border-white/30 text-white hover:border-white/60 transition-all duration-300">
              Browse All Services
            </button>
          </div>
        </div>
      </div>

      <PortfolioFooter />
    </div>
  );
};

export default SoftwareLanding;
